import axios from 'axios';
import type { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import { getActivePinia } from 'pinia';
import { API_EC } from '@/config/api.config';
import { useAppLoadingStore } from '@/stores/loading.store';
import { getEnvConfig } from '@/config/mount-env';

const AXIOS_TIMEOUT = 60000;

type RefreshSubscriber = () => void;

type RetryRequestConfig = AxiosRequestConfig & {
    _retry?: boolean;
};

type PrimitiveValue = string | number | boolean | null;

type MallRequestPayload = Record<string, PrimitiveValue | undefined>;

type MallRequestData = {
    Timestamp?: string;
    RequestId?: string;
    Lang?: string;
    Source?: 'MobileWeb' | 'PcWeb';
    Token?: string;
    Data?: MallRequestPayload;
    [key: string]: PrimitiveValue | MallRequestPayload | undefined;
};

/**
 * 取得防偽驗證 token
 */
const getRequestVerificationToken = (): string => {
    const tokenInput = document.querySelector(
        'input[name="__RequestVerificationToken"]'
    ) as HTMLInputElement | null;

    return tokenInput?.value || '';
};

/**
 * 取得 cookie map
 */
const getCookieMap = (): Record<string, string> => {
    if (!document.cookie) return {};

    return document.cookie
        .split('; ')
        .reduce<Record<string, string>>((acc, cookie) => {
            const [key, value] = cookie.split('=');
            if (key) {
                acc[key] = value || '';
            }
            return acc;
        }, {});
};

/**
 * 產生 request id
 */
const createRequestId = (): string => {
    if (
        typeof crypto !== 'undefined' &&
        typeof crypto.randomUUID === 'function'
    ) {
        return crypto.randomUUID();
    }

    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

/**
 * 解析裝置來源
 */
const resolveClientSource = (): 'MobileWeb' | 'PcWeb' => {
    return typeof isMobile !== 'undefined' && isMobile ? 'MobileWeb' : 'PcWeb';
};

/**
 * 正規化 Mall request data
 */
const resolveMallRequestData = (
    data: AxiosRequestConfig['data']
): MallRequestData => {
    if (data && typeof data === 'object' && !Array.isArray(data)) {
        return data as MallRequestData;
    }

    return {};
};

/**
 * 建立 axios instance
 */
const createAxiosInstance = (baseURL: string): AxiosInstance => {
    return axios.create({
        baseURL,
        timeout: AXIOS_TIMEOUT,
        withCredentials: true,
    });
};

/**
 * 登出流程
 */
const logout = async (): Promise<void> => {
    try {
        await axios.get('/Account/LogOut', {
            withCredentials: true,
        });
    } catch (error) {
        console.warn('登出失敗:', error);
    } finally {
        document.cookie = 'loggedout=1; path=/';
    }
};

/**
 * 建立 loading 控制器，visibleFlag 控制是否同時顯示全站載入遮罩
 */
const createLoadingController = (
    visibleFlag = true
): {
    startLoading: () => void;
    endLoading: () => void;
} => {
    const resolveLoadingStore = () => {
        if (!getActivePinia()) {
            return null;
        }

        return useAppLoadingStore();
    };

    const startLoading = (): void => {
        const loadingStore = resolveLoadingStore();
        if (!loadingStore) return;

        loadingStore.start({ visibleFlag });
    };

    const endLoading = (): void => {
        const loadingStore = resolveLoadingStore();
        if (!loadingStore) return;

        loadingStore.end({ visibleFlag });
    };

    return {
        startLoading,
        endLoading,
    };
};

/**
 * 設定共用 headers
 */
const setupCommonHeaders = (instance: AxiosInstance): void => {
    instance.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';
    instance.defaults.headers.common['X-Axios-Request'] = 'true';

    const token = getRequestVerificationToken();
    if (token) {
        instance.defaults.headers.common['RequestVerificationToken'] = token;
    }
};

/**
 * 設定 EC instance interceptors
 */
const setupEcInterceptors = (input: {
    axiosEc: AxiosInstance;
    startLoading: () => void;
    endLoading: () => void;
}): void => {
    let isRefreshing = false;
    let refreshQueue: RefreshSubscriber[] = [];

    const subscribeRefresh = (callback: RefreshSubscriber): void => {
        refreshQueue.push(callback);
    };

    const notifyRefreshSubscribers = (): void => {
        refreshQueue.forEach((callback) => callback());
        refreshQueue = [];
    };

    input.axiosEc.interceptors.request.use(
        (config) => {
            if (
                config.url !== API_EC.REFRESH_TOKEN &&
                !(config as RetryRequestConfig)._retry
            ) {
                input.startLoading();
            }

            return config;
        },
        (error) => {
            input.endLoading();
            return Promise.reject(error);
        }
    );

    input.axiosEc.interceptors.response.use(
        (response) => {
            if (response.config.url !== API_EC.REFRESH_TOKEN) {
                input.endLoading();
            }
            return response;
        },
        (error: AxiosError) => {
            const requestConfig = (error.config || {}) as RetryRequestConfig;
            const statusCode = error.response?.status;

            if (statusCode === 401 && !requestConfig._retry) {
                if (!isRefreshing) {
                    isRefreshing = true;

                    return axios
                        .post(
                            API_EC.REFRESH_TOKEN,
                            {},
                            {
                                withCredentials: true,
                            }
                        )
                        .then(() => {
                            notifyRefreshSubscribers();
                            isRefreshing = false;

                            requestConfig._retry = true;
                            return input.axiosEc(requestConfig);
                        })
                        .catch(async (refreshError) => {
                            input.startLoading();
                            await logout();
                            refreshQueue = [];
                            isRefreshing = false;

                            setTimeout(() => {
                                window.location.href = '/Home/Index';
                            }, 1000);

                            return Promise.reject(refreshError);
                        });
                }

                return new Promise((resolve) => {
                    subscribeRefresh(() => {
                        requestConfig._retry = true;
                        resolve(input.axiosEc(requestConfig));
                    });
                });
            }

            if (requestConfig.url !== API_EC.REFRESH_TOKEN) {
                input.endLoading();
            }

            return Promise.reject(error);
        }
    );
};

/**
 * 設定 Mall instance interceptors
 */
const setupMallInterceptors = (input: {
    axiosMall: AxiosInstance;
    startLoading: () => void;
    endLoading: () => void;
}): void => {
    input.axiosMall.interceptors.request.use(
        async (config) => {
            const envConfig = getEnvConfig();
            config.baseURL = envConfig.MALL_BASE_URL;
            input.startLoading();
            config.withCredentials = true;

            const method = (config.method || '').toLowerCase();
            if (method === 'post' || method === 'put') {
                const timestampResponse = await axios.post(
                    `${envConfig.OAUTH_BASE_URL}/timestamps`,
                    {
                        Lang: 'zh-TW',
                        RequestId: createRequestId(),
                    }
                );

                const requestPath = new URL(
                    config.url || '',
                    window.location.origin
                ).pathname;
                const specialShopUrls: string[] = [];
                const noTokenUrls: string[] = [];

                const data = resolveMallRequestData(config.data);
                data.Timestamp = timestampResponse.data?.Data?.Timestamp || '';
                data.RequestId = createRequestId();
                data.Lang = 'zh-TW';

                if (data.Data && !window.location.href.includes('ibon-web')) {
                    const shopCode = envConfig.VUE_APP_SHOP_CODE;
                    const shopCode2 = envConfig.VUE_APP_SHOP_CODE_2;

                    if (
                        data.Data.ShopCode !== shopCode2 &&
                        data.Data.ShopCodes !== shopCode2
                    ) {
                        data.Data.ShopCode = shopCode;
                        data.Data.ShopCodes = shopCode;
                    } else {
                        data.Data.ShopCode = shopCode2;
                        data.Data.ShopCodes = shopCode2;
                    }

                    if (specialShopUrls.includes(requestPath)) {
                        data.Data.ShopCode = shopCode2;
                        data.Data.ShopCodes = `${shopCode2},${shopCode}`;
                    }
                }

                data.Source = resolveClientSource();

                if (!noTokenUrls.includes(requestPath)) {
                    const tokenMap = getCookieMap();
                    if (tokenMap['DJIT-Token']) {
                        data.Token = tokenMap['DJIT-Token'];
                    }
                }

                config.data = data;
            }

            return config;
        },
        (error) => {
            input.endLoading();
            return Promise.reject(error);
        }
    );

    input.axiosMall.interceptors.response.use(
        (response) => {
            input.endLoading();
            return response;
        },
        (error) => {
            input.endLoading();
            return Promise.reject(error);
        }
    );
};

/**
 * 設定 Lite instance interceptors
 */
const setupLiteInterceptors = (input: {
    axiosLite: AxiosInstance;
    startLoading: () => void;
    endLoading: () => void;
}): void => {
    input.axiosLite.interceptors.request.use(
        (config) => {
            input.startLoading();
            return config;
        },
        (error) => {
            input.endLoading();
            return Promise.reject(error);
        }
    );

    input.axiosLite.interceptors.response.use(
        (response) => {
            input.endLoading();
            return response;
        },
        (error) => {
            input.endLoading();
            return Promise.reject(error);
        }
    );
};

/**
 * 建立 EC axios instance factory
 */
export const createEcAxiosFactory = (input: {
    startLoading: () => void;
    endLoading: () => void;
}): AxiosInstance => {
    const axiosEc = createAxiosInstance('/');
    setupCommonHeaders(axiosEc);
    setupEcInterceptors({
        axiosEc,
        startLoading: input.startLoading,
        endLoading: input.endLoading,
    });

    return axiosEc;
};

/**
 * 建立 Mall axios instance factory
 */
export const createMallAxiosFactory = (input: {
    startLoading: () => void;
    endLoading: () => void;
}): AxiosInstance => {
    const axiosMall = createAxiosInstance('');
    setupCommonHeaders(axiosMall);
    setupMallInterceptors({
        axiosMall,
        startLoading: input.startLoading,
        endLoading: input.endLoading,
    });

    return axiosMall;
};

/**
 * 建立輕量 axios instance factory：不含 token refresh，僅累計 loading 計數
 */
export const createLiteAxiosFactory = (input: {
    startLoading: () => void;
    endLoading: () => void;
}): AxiosInstance => {
    const instance = createAxiosInstance('/');
    setupCommonHeaders(instance);
    setupLiteInterceptors({
        axiosLite: instance,
        startLoading: input.startLoading,
        endLoading: input.endLoading,
    });

    return instance;
};

const loadingController = createLoadingController();
// lite instance 專用：只累計 loading 計數，不顯示全站遮罩
const silentLoadingController = createLoadingController(false);

/**
 * 內部共用 EC axios 實例
 */
export const axiosEc = createEcAxiosFactory({
    startLoading: loadingController.startLoading,
    endLoading: loadingController.endLoading,
});

/**
 * 內部共用 Mall axios 實例
 */
export const axiosMall = createMallAxiosFactory({
    startLoading: loadingController.startLoading,
    endLoading: loadingController.endLoading,
});

/**
 * 內部共用輕量 axios 實例，不含 token refresh；
 * 累計 loading 計數但不顯示全站載入遮罩
 */
export const axiosLite = createLiteAxiosFactory({
    startLoading: silentLoadingController.startLoading,
    endLoading: silentLoadingController.endLoading,
});
